<template>
  <div class="inviteCsvUpload">
    <label class="uploadLabel" for="inviteCsvFile">Upload a CSV of invites</label>
    <div class="uploadRow">
      <input id="inviteCsvFile"
             ref="fileInput"
             type="file"
             accept=".csv,text/csv"
             @change="onFileSelected" />
      <span v-if="selectedFileName" class="fileName">{{ selectedFileName }}</span>
    </div>

    <p class="uploadHint">
      If your CSV uses the standard invite column headers it loads as-is. Otherwise, map each invite field to one of
      your columns below and select Validate Mappings — we support any column layout. Cell values are checked after
      upload and anything that cannot be read is highlighted in the grid for you to correct.
    </p>

    <div class="mappingSection">
      <span class="mappingLabel">Mappings</span>
      <p v-if="!selectedFile" class="mappingPlaceholder">Select a CSV to map its columns.</p>
      <InviteCsvMappingForm v-else :headers="headers" @validated="onMappingValidated" />
    </div>
  </div>
</template>

<script lang="ts">
import * as Vue from 'vue';
import { parseInvitesCsv, getCsvHeaders, AdminInvitesBatchError } from '../api/endpoints/adminInvitesWeb';
import InviteCsvMappingForm from './InviteCsvMappingForm.vue';
import { InviteColumns } from '@/constants/inviteColumns';
import { CsvHeader, InviteColumnMapping, ParseInvitesResult } from '@/types/adminInvites';

// MM-42243: file picker for the bulk-invite grid. It owns the pick-parse step and the per-upload column mapping. On
// selection it reads the file's columns (:headers). A file that already carries every canonical header is parsed
// (:parse) immediately; any other layout is held until the operator maps its columns in the inline "Mappings" subform
// and validates, at which point the SAME file is parsed through a server-side DataWeaver transform that projects the
// client's columns onto our canonical shape. Holding the parse keeps the server's missing-header rejection from being
// shown for a file that simply needs mapping. The mapping is per-upload only and is reset when a new file is chosen.
// Whole-batch rejections are surfaced via `batch-error` for the page to show inline, and `file-selected` lets the page
// clear a previous file's error; the page remains the sole owner of grid state. The mapping never rides to :load — the
// grid re-serializes canonical rows there — so it is contained here.
export default Vue.defineComponent({
  name: 'InviteCsvUpload',
  components: { InviteCsvMappingForm },
  emits: {
    'file-selected': () => true,
    parsed: (_result: ParseInvitesResult) => true,
    'batch-error': (_messages: string[]) => true
  },
  data() {
    return {
      selectedFile: null as File | null,
      selectedFileName: '' as string,
      headers: [] as CsvHeader[],
      mapping: null as InviteColumnMapping | null
    };
  },
  methods: {
    async onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      // Reset the native input immediately so re-selecting the same file still fires change.
      input.value = '';
      if (!file)
        return;

      // A new file starts a fresh mapping — never carry a previous file's column choices forward.
      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.headers = [];
      this.mapping = null;
      this.$emit('file-selected');

      const headersLoaded = await this.loadHeaders(file);

      // Parse straight away when the file already uses our layout, or when its headers could not be read at all (the
      // parse then surfaces the real batch error, e.g. empty or oversize). Otherwise wait for a validated mapping.
      if (!headersLoaded || this.hasAllCanonicalHeaders())
        await this.reparse();
    },

    // Reads the file's columns for the mapping subform. Returns false on failure, leaving the subform empty (it shows
    // an empty-state message) so the caller can fall back to the parse, which reports the real batch error.
    async loadHeaders(file: File): Promise<boolean> {
      try {
        const result = await getCsvHeaders(file);
        this.headers = result.headers ?? [];
        return true;
      } catch {
        this.headers = [];
        return false;
      }
    },

    // True when every canonical column (hidden ones included — the server requires all of them for header presence)
    // appears in the file's header row, compared case-insensitively as the server's header lookup does.
    hasAllCanonicalHeaders(): boolean {
      const present = new Set(this.headers.map(h => (h.original || '').trim().toLowerCase()));
      return InviteColumns.every(col => present.has(col.header.toLowerCase()));
    },

    // Parses the retained file with the current mapping (null until the operator validates one) and emits the outcome.
    async reparse() {
      if (!this.selectedFile)
        return;

      try {
        const result = await parseInvitesCsv(this.selectedFile, this.mapping ?? undefined);
        this.$emit('parsed', result);
      } catch (error) {
        if (error instanceof AdminInvitesBatchError)
          this.$emit('batch-error', error.errors.map(e => e.localizedName));
        else
          this.$emit('batch-error', ['The CSV could not be uploaded. Please try again.']);
      }
    },

    async onMappingValidated(mapping: InviteColumnMapping) {
      this.mapping = mapping;
      await this.reparse();
    }
  }
});
</script>

<style lang="scss">
.inviteCsvUpload {

  .uploadLabel {
    font-weight: $font_weight_bold;
    display: block;
    margin-bottom: 6px;
  }

  .uploadRow {
    display: flex;
    align-items: center;
    gap: 10px;

    // The global input style (large font, border, gray fill) applies to the file input too; give it a fixed height
    // and center its "Choose file" button and file-name text vertically within it.
    input[type=file] {
      display: inline-flex;
      align-items: center;
      height: 44px;
      padding: 0 8px;
      font-size: 1rem;
      line-height: normal;
      box-sizing: border-box;

      &::file-selector-button {
        align-self: center;
        margin: 0 10px 0 0;
        padding: 4px 14px;
        vertical-align: middle;
        cursor: pointer;
      }
    }
  }

  .mappingSection {
    margin-top: 12px;
  }

  .mappingLabel {
    font-weight: $font_weight_bold;
    display: block;
  }

  .mappingPlaceholder {
    margin-top: 4px;
    color: $faint_text;
    font-size: 0.9rem;
  }

  .fileName {
    color: $faint_text;
  }

  .uploadHint {
    margin-top: 8px;
    color: $faint_text;
    font-size: 0.9rem;
  }
}
</style>
